import React, { Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { useAuthStore } from './store/authStore';
import { useIdleLogout } from './hooks/useIdleLogout';
import api from './api';
import Layout from './components/Layout';
import SettingsLayout from './components/SettingsLayout';
import PublicLayout from './components/PublicLayout';
import './App.css';

// ── Lazy page chunks ─────────────────────────────────────────────────────────
const Login               = React.lazy(() => import('./pages/Login'));
const Register            = React.lazy(() => import('./pages/Register'));
const ForgotPassword      = React.lazy(() => import('./pages/ForgotPassword'));
const ResetPassword       = React.lazy(() => import('./pages/ResetPassword'));
const AcceptInvite        = React.lazy(() => import('./pages/AcceptInvite'));

// Marketing
const Landing             = React.lazy(() => import('./pages/Landing'));
const Features            = React.lazy(() => import('./pages/Features'));
const Pricing             = React.lazy(() => import('./pages/Pricing'));
const Contact             = React.lazy(() => import('./pages/Contact'));
const CareerPage          = React.lazy(() => import('./pages/CareerPage'));

// Legal / trust — must stay reachable while signed out (app-store review needs
// a publicly resolvable privacy policy URL).
const Privacy             = React.lazy(() => import('./pages/Privacy'));
const Terms               = React.lazy(() => import('./pages/Terms'));
const DataDeletion        = React.lazy(() => import('./pages/DataDeletion'));
const Support             = React.lazy(() => import('./pages/Support'));
const SecurityOverview    = React.lazy(() => import('./pages/SecurityOverview'));

// Core HR
const Dashboard           = React.lazy(() => import('./pages/Dashboard'));
const EmployeeDashboard   = React.lazy(() => import('./pages/EmployeeDashboard'));
const Employees           = React.lazy(() => import('./pages/Employees'));
const Attendance          = React.lazy(() => import('./pages/Attendance'));
const MyAttendance        = React.lazy(() => import('./pages/MyAttendance'));
const Leaves              = React.lazy(() => import('./pages/Leaves'));
const Regularizations     = React.lazy(() => import('./pages/Regularizations'));
const Bonus               = React.lazy(() => import('./pages/Bonus'));
const Advance             = React.lazy(() => import('./pages/Advance'));
const Payroll             = React.lazy(() => import('./pages/Payroll'));
const PayRuns             = React.lazy(() => import('./pages/PayRuns'));
const AIInsights          = React.lazy(() => import('./pages/AIInsights'));
const ReportBuilder       = React.lazy(() => import('./pages/ReportBuilder'));
const PluginMarketplace   = React.lazy(() => import('./pages/PluginMarketplace'));
const WhatsappSettings    = React.lazy(() => import('./pages/settings/Whatsapp'));
const MobileAppSettings   = React.lazy(() => import('./pages/settings/MobileApp'));
const Reports             = React.lazy(() => import('./pages/Reports'));
const Payslip             = React.lazy(() => import('./pages/Payslip'));
const MyPayslips          = React.lazy(() => import('./pages/MyPayslips'));
const MyBonus             = React.lazy(() => import('./pages/MyBonus'));
const MyTimesheet         = React.lazy(() => import('./pages/MyTimesheet'));
const Projects            = React.lazy(() => import('./pages/Projects'));
const Tasks               = React.lazy(() => import('./pages/Tasks'));
const MyTasks             = React.lazy(() => import('./pages/MyTasks'));
const MyProfile           = React.lazy(() => import('./pages/MyProfile'));
const MyAdvance           = React.lazy(() => import('./pages/MyAdvance'));
const MyDocuments         = React.lazy(() => import('./pages/MyDocuments'));
const TaxDeclarations     = React.lazy(() => import('./pages/TaxDeclarations'));
const Reimbursements      = React.lazy(() => import('./pages/Reimbursements'));
const Approvals           = React.lazy(() => import('./pages/Approvals'));
const Shifts              = React.lazy(() => import('./pages/Shifts'));
const Analytics           = React.lazy(() => import('./pages/Analytics'));
const Documents           = React.lazy(() => import('./pages/Documents'));
const MySignatures        = React.lazy(() => import('./pages/MySignatures'));
const Recruitment         = React.lazy(() => import('./pages/Recruitment'));
const Performance         = React.lazy(() => import('./pages/Performance'));
const MyReviews           = React.lazy(() => import('./pages/MyReviews'));
const SalaryGrades        = React.lazy(() => import('./pages/SalaryGrades'));
const Holidays            = React.lazy(() => import('./pages/Holidays'));
const Users               = React.lazy(() => import('./pages/Users'));
const Companies           = React.lazy(() => import('./pages/Companies'));
const Security            = React.lazy(() => import('./pages/Security'));
const AuditLogs           = React.lazy(() => import('./pages/AuditLogs'));
const Billing             = React.lazy(() => import('./pages/Billing'));
const Directory           = React.lazy(() => import('./pages/Directory'));
const Announcements       = React.lazy(() => import('./pages/Announcements'));
const Resignations        = React.lazy(() => import('./pages/Resignations'));
const OrgChart            = React.lazy(() => import('./pages/OrgChart'));

// Phase 3
const Growth              = React.lazy(() => import('./pages/Growth'));
const Loans               = React.lazy(() => import('./pages/Loans'));
const SalaryRevisions     = React.lazy(() => import('./pages/SalaryRevisions'));
const Assets              = React.lazy(() => import('./pages/Assets'));
const Letters             = React.lazy(() => import('./pages/Letters'));
const Travel              = React.lazy(() => import('./pages/Travel'));
const MyTravel            = React.lazy(() => import('./pages/MyTravel'));
const Drive               = React.lazy(() => import('./pages/Drive'));
const Helpdesk            = React.lazy(() => import('./pages/Helpdesk'));
const Learning            = React.lazy(() => import('./pages/Learning'));
const Surveys             = React.lazy(() => import('./pages/Surveys'));
const BiometricDevices    = React.lazy(() => import('./pages/BiometricDevices'));
const FaceAttendance      = React.lazy(() => import('./pages/FaceAttendance'));
const CompOff             = React.lazy(() => import('./pages/CompOff'));
const Workflows           = React.lazy(() => import('./pages/Workflows'));
const DocumentExpiry      = React.lazy(() => import('./pages/DocumentExpiry'));
const InvestmentProofs    = React.lazy(() => import('./pages/InvestmentProofs'));

// Phase 4
const Locations           = React.lazy(() => import('./pages/Locations'));
const CustomFields        = React.lazy(() => import('./pages/CustomFields'));
const WebhooksAndTokens   = React.lazy(() => import('./pages/WebhooksAndTokens'));
// Phase4Misc named exports wrapped individually so webpack can split them
const lazy4 = (name) => React.lazy(() => import('./pages/Phase4Misc').then(m => ({ default: m[name] })));
const ReimbursementCategories = lazy4('ReimbursementCategories');
const SalaryTemplates         = lazy4('SalaryTemplates');
const OtPolicies              = lazy4('OtPolicies');
const TalentPool              = lazy4('TalentPool');
const AccountingExport        = lazy4('AccountingExport');

// Phase 5
const ShiftSwaps          = React.lazy(() => import('./pages/ShiftSwaps'));
const Skills              = React.lazy(() => import('./pages/Skills'));
const MyPeerReviews       = React.lazy(() => import('./pages/MyPeerReviews'));
const InternalJobs        = React.lazy(() => import('./pages/InternalJobs'));

// Parity gaps (migration 0037)
const Fbp                 = React.lazy(() => import('./pages/Fbp'));
const MyFbp               = React.lazy(() => import('./pages/MyFbp'));

// Settings pages
const GeneralSettings         = React.lazy(() => import('./pages/settings/General'));
const PayrollCalcSettings     = React.lazy(() => import('./pages/settings/PayrollCalc'));
const PfSettings              = React.lazy(() => import('./pages/settings/Pf'));
const EsiSettings             = React.lazy(() => import('./pages/settings/Esi'));
const PayslipTemplateSettings = React.lazy(() => import('./pages/settings/PayslipTemplate'));
const PayoutsSettings         = React.lazy(() => import('./pages/settings/Payouts'));
const AttendanceSettings      = React.lazy(() => import('./pages/settings/Attendance'));
const SalaryComponents        = React.lazy(() => import('./pages/SalaryComponents'));
const LeaveTypes              = React.lazy(() => import('./pages/LeaveTypes'));
const Lookups                 = React.lazy(() => import('./pages/Lookups'));
const Checklists              = React.lazy(() => import('./pages/Checklists'));
const RolesPermissions        = React.lazy(() => import('./pages/RolesPermissions'));
const ApprovalChains          = React.lazy(() => import('./pages/ApprovalChains'));

// Admin
const PlansAdmin          = React.lazy(() => import('./pages/admin/PlansAdmin'));
const SubscriptionsAdmin  = React.lazy(() => import('./pages/admin/SubscriptionsAdmin'));
const CouponsAdmin        = React.lazy(() => import('./pages/admin/Coupons'));
const PlatformStats       = React.lazy(() => import('./pages/admin/PlatformStats'));

// ── Shared loading fallback ───────────────────────────────────────────────────
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center text-slate-500">
    Loading…
  </div>
);

// ── Route guards ─────────────────────────────────────────────────────────────
// Paths where we don't bother probing /auth/me at boot — marketing pages
// shouldn't trigger an authenticated request just to render.
const PUBLIC_PATHS = new Set(['/', '/features', '/pricing', '/contact', '/support', '/careers', '/privacy', '/terms', '/data-deletion', '/security', '/forgot-password', '/reset-password', '/accept-invite']);

const ProtectedRoute = ({ children }) => {
  const user = useAuthStore(s => s.user);
  const hydrated = useAuthStore(s => s.hydrated);
  if (!hydrated) return <PageLoader />;
  return user ? children : <Navigate to="/login" replace />;
};

// Accepts EITHER:
//   • roles={[...]}       — legacy enum match (self-service routes)
//   • permissions={[...]} — RBAC check: pass if user has ANY of the listed keys
// If both provided, EITHER passing is enough (OR).
const RoleRoute = ({ roles, permissions, children }) => {
  const user = useAuthStore(s => s.user);
  if (!user) return null;
  const userPerms = user.permissions || [];
  const hasPerm = permissions?.length > 0 && permissions.some(p => userPerms.includes(p));
  const hasRole = roles?.length > 0 && roles.includes(user.role);
  if (hasPerm || hasRole) return children;
  return <Navigate to="/dashboard" replace />;
};

const DashboardRouter = () => {
  const user = useAuthStore(s => s.user);
  return user?.role === 'employee' ? <EmployeeDashboard /> : <Dashboard />;
};

// ── App ───────────────────────────────────────────────────────────────────────
function App() {
  const setUser = useAuthStore(s => s.setUser);
  const clear   = useAuthStore(s => s.clear);
  useIdleLogout();

  useEffect(() => {
    if (PUBLIC_PATHS.has(window.location.pathname)) { clear(); return undefined; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/auth/me');
        if (!cancelled) setUser(data);
      } catch (_) {
        if (!cancelled) clear();
      }
    })();
    return () => { cancelled = true; };
  }, [setUser, clear]);

  return (
    <BrowserRouter>
      <Toaster position="top-right" richColors />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public marketing pages */}
          <Route element={<PublicLayout />}>
            <Route path="/" element={<Landing />} />
            <Route path="/features" element={<Features />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/contact" element={<Contact />} />
            {/* Support — public and unauthenticated. This is the Support URL
                given to App Store Connect and Play Console, so it must resolve
                for a logged-out reviewer. */}
            <Route path="/support" element={<Support />} />
            {/* Legal — intentionally unauthenticated. `/security` here is the public
                trust page; the signed-in account-security screen lives at
                /account/security so the two don't collide on the same path. */}
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/data-deletion" element={<DataDeletion />} />
            <Route path="/security" element={<SecurityOverview />} />
          </Route>

          {/* Public careers page — unauthenticated, accepts ?c=COMPANY_ID */}
          <Route path="/careers" element={<CareerPage />} />

          {/* Auth pages */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/accept-invite" element={<AcceptInvite />} />

          {/* Authenticated app */}
          <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
            <Route path="dashboard" element={<DashboardRouter />} />
            <Route path="employees" element={<RoleRoute roles={['super_admin', 'company_admin', 'hr', 'manager', 'accountant']}><Employees /></RoleRoute>} />
            <Route path="attendance" element={<RoleRoute roles={['super_admin', 'company_admin', 'hr', 'manager', 'accountant']}><Attendance /></RoleRoute>} />
            <Route path="my-attendance" element={<MyAttendance />} />
            <Route path="leaves" element={<Leaves />} />
            <Route path="regularizations" element={<Regularizations />} />
            <Route path="bonus" element={<RoleRoute roles={['super_admin', 'company_admin', 'hr', 'manager', 'accountant']}><Bonus /></RoleRoute>} />
            <Route path="advance" element={<RoleRoute roles={['super_admin', 'company_admin', 'hr', 'accountant']}><Advance /></RoleRoute>} />
            <Route path="payroll" element={<RoleRoute roles={['super_admin', 'company_admin', 'hr', 'manager', 'accountant']}><Payroll /></RoleRoute>} />
            <Route path="pay-runs" element={<RoleRoute roles={['super_admin', 'company_admin', 'hr', 'accountant']} permissions={['pay_runs.view']}><PayRuns /></RoleRoute>} />
            <Route path="pay-runs/:id" element={<RoleRoute roles={['super_admin', 'company_admin', 'hr', 'accountant']} permissions={['pay_runs.view']}><PayRuns /></RoleRoute>} />
            <Route path="ai-insights" element={<RoleRoute roles={['super_admin', 'company_admin', 'hr', 'accountant']} permissions={['payroll.view']}><AIInsights /></RoleRoute>} />
            <Route path="report-builder" element={<RoleRoute roles={['super_admin', 'company_admin', 'hr', 'accountant', 'manager']} permissions={['reports.build']}><ReportBuilder /></RoleRoute>} />
            <Route path="plugins" element={<RoleRoute roles={['super_admin', 'company_admin', 'hr']} permissions={['plugins.view']}><PluginMarketplace /></RoleRoute>} />
            <Route path="reports" element={<RoleRoute roles={['super_admin', 'company_admin', 'hr', 'manager', 'accountant']}><Reports /></RoleRoute>} />
            <Route path="payslip/:id" element={<Payslip />} />
            <Route path="my-payslips" element={<MyPayslips />} />
            <Route path="my-bonus" element={<MyBonus />} />
            <Route path="my-timesheet" element={<MyTimesheet />} />
            <Route path="projects" element={<Projects />} />
            <Route path="tasks" element={<RoleRoute roles={['super_admin', 'company_admin', 'hr', 'manager']}><Tasks /></RoleRoute>} />
            <Route path="my-tasks" element={<MyTasks />} />
            <Route path="my-profile" element={<MyProfile />} />
            <Route path="my-advance" element={<MyAdvance />} />
            <Route path="my-travel" element={<MyTravel />} />
            <Route path="drive" element={<Drive />} />
            <Route path="my-documents" element={<MyDocuments />} />
            <Route path="tax-declarations" element={<TaxDeclarations />} />
            <Route path="reimbursements" element={<Reimbursements />} />
            <Route path="approvals" element={<RoleRoute roles={['super_admin', 'company_admin', 'hr', 'manager', 'accountant']}><Approvals /></RoleRoute>} />
            <Route path="shifts" element={<RoleRoute roles={['super_admin', 'company_admin', 'hr', 'manager']}><Shifts /></RoleRoute>} />

            {/* Legacy redirects — keep bookmarks alive */}
            <Route path="checklists"      element={<Navigate to="/settings/checklists" replace />} />
            <Route path="approval-chains" element={<Navigate to="/settings/approval-chains" replace />} />
            <Route path="roles"           element={<Navigate to="/settings/roles" replace />} />
            <Route path="leave-types"     element={<Navigate to="/settings/leave-types" replace />} />
            <Route path="salary-components" element={<Navigate to="/settings/salary-components" replace />} />
            <Route path="lookups"         element={<Navigate to="/settings/departments" replace />} />

            <Route path="analytics"   element={<RoleRoute roles={['super_admin', 'company_admin', 'hr', 'manager', 'accountant']} permissions={['payroll.view']}><Analytics /></RoleRoute>} />
            <Route path="documents"   element={<RoleRoute roles={['super_admin', 'company_admin', 'hr']} permissions={['documents.signature.manage']}><Documents /></RoleRoute>} />
            <Route path="recruitment" element={<RoleRoute roles={['super_admin', 'company_admin', 'hr']} permissions={['recruitment.manage']}><Recruitment /></RoleRoute>} />
            <Route path="performance" element={<RoleRoute roles={['super_admin', 'company_admin', 'hr', 'manager']} permissions={['performance.manage']}><Performance /></RoleRoute>} />
            <Route path="my-reviews"    element={<MyReviews />} />
            <Route path="my-signatures" element={<MySignatures />} />
            <Route path="companies"     element={<RoleRoute roles={['super_admin']}><Companies /></RoleRoute>} />
            <Route path="users"         element={<RoleRoute roles={['super_admin', 'company_admin']}><Users /></RoleRoute>} />
            <Route path="salary-grades" element={<RoleRoute roles={['super_admin', 'company_admin', 'hr']}><SalaryGrades /></RoleRoute>} />
            <Route path="holidays"      element={<Holidays />} />

            {/* Settings hub */}
            <Route
              path="settings"
              element={<RoleRoute roles={['super_admin', 'company_admin', 'hr']}><SettingsLayout /></RoleRoute>}
            >
              <Route index element={<GeneralSettings />} />
              <Route path="payroll-calc"     element={<RoleRoute roles={['super_admin', 'company_admin', 'hr']}><PayrollCalcSettings /></RoleRoute>} />
              <Route path="pf"               element={<RoleRoute roles={['super_admin', 'company_admin', 'hr']}><PfSettings /></RoleRoute>} />
              <Route path="esi"              element={<RoleRoute roles={['super_admin', 'company_admin', 'hr']}><EsiSettings /></RoleRoute>} />
              <Route path="payslip-template" element={<RoleRoute roles={['super_admin', 'company_admin', 'hr']}><PayslipTemplateSettings /></RoleRoute>} />
              <Route path="payouts"          element={<RoleRoute roles={['super_admin', 'company_admin']}><PayoutsSettings /></RoleRoute>} />
              <Route path="whatsapp"         element={<RoleRoute roles={['super_admin', 'company_admin']}><WhatsappSettings /></RoleRoute>} />
              <Route path="mobile-app"       element={<RoleRoute roles={['super_admin', 'company_admin', 'hr']}><MobileAppSettings /></RoleRoute>} />
              <Route path="attendance"       element={<RoleRoute roles={['super_admin', 'company_admin', 'hr']}><AttendanceSettings /></RoleRoute>} />
              <Route path="salary-components" element={<RoleRoute roles={['super_admin', 'company_admin', 'hr']}><SalaryComponents /></RoleRoute>} />
              <Route path="leave-types"      element={<RoleRoute roles={['super_admin', 'company_admin', 'hr']}><LeaveTypes /></RoleRoute>} />
              <Route path="departments"      element={<RoleRoute roles={['super_admin', 'company_admin', 'hr']}><Lookups /></RoleRoute>} />
              <Route path="checklists"       element={<RoleRoute roles={['super_admin', 'company_admin', 'hr']}><Checklists /></RoleRoute>} />
              <Route path="roles"            element={<RoleRoute roles={['super_admin', 'company_admin']}><RolesPermissions /></RoleRoute>} />
              <Route path="approval-chains"  element={<RoleRoute roles={['super_admin', 'company_admin']}><ApprovalChains /></RoleRoute>} />
            </Route>

            {/* Moved off /security — that path is now the public trust page. */}
            <Route path="account/security" element={<Security />} />
            <Route path="audit-logs" element={<RoleRoute roles={['super_admin', 'company_admin']}><AuditLogs /></RoleRoute>} />
            <Route path="billing"    element={<RoleRoute roles={['super_admin', 'company_admin']}><Billing /></RoleRoute>} />
            <Route path="admin/stats"         element={<RoleRoute roles={['super_admin']}><PlatformStats /></RoleRoute>} />
            <Route path="admin/plans"         element={<RoleRoute roles={['super_admin']}><PlansAdmin /></RoleRoute>} />
            <Route path="admin/subscriptions" element={<RoleRoute roles={['super_admin']}><SubscriptionsAdmin /></RoleRoute>} />
            <Route path="admin/coupons"       element={<RoleRoute roles={['super_admin']}><CouponsAdmin /></RoleRoute>} />

            {/* Phase 3 */}
            <Route path="growth"           element={<Growth />} />
            <Route path="loans"            element={<RoleRoute roles={['super_admin', 'company_admin', 'hr', 'accountant']}><Loans /></RoleRoute>} />
            <Route path="salary-revisions" element={<RoleRoute roles={['super_admin', 'company_admin', 'hr']}><SalaryRevisions /></RoleRoute>} />
            <Route path="investment-proofs" element={<RoleRoute roles={['super_admin', 'company_admin', 'hr', 'accountant']}><InvestmentProofs /></RoleRoute>} />
            <Route path="letters"          element={<RoleRoute roles={['super_admin', 'company_admin', 'hr']} permissions={['letters.manage']}><Letters /></RoleRoute>} />
            <Route path="assets"           element={<RoleRoute roles={['super_admin', 'company_admin', 'hr']} permissions={['assets.manage']}><Assets /></RoleRoute>} />
            <Route path="travel"           element={<Travel />} />
            <Route path="helpdesk"         element={<Helpdesk />} />
            <Route path="learning"         element={<Learning />} />
            <Route path="surveys"          element={<Surveys />} />
            <Route path="org-chart"        element={<OrgChart />} />
            <Route path="biometric"        element={<RoleRoute roles={['super_admin', 'company_admin']} permissions={['biometric.manage']}><BiometricDevices /></RoleRoute>} />
            <Route path="face-attendance"  element={<RoleRoute roles={['super_admin', 'company_admin', 'hr']} permissions={['face.manage']}><FaceAttendance /></RoleRoute>} />
            <Route path="comp-off"         element={<RoleRoute roles={['super_admin', 'company_admin', 'hr', 'manager']}><CompOff /></RoleRoute>} />
            <Route path="workflows"        element={<RoleRoute roles={['super_admin', 'company_admin']} permissions={['workflows.manage']}><Workflows /></RoleRoute>} />
            <Route path="document-expiry"  element={<RoleRoute roles={['super_admin', 'company_admin', 'hr']}><DocumentExpiry /></RoleRoute>} />

            {/* Phase 4 */}
            <Route path="locations"       element={<RoleRoute roles={['super_admin', 'company_admin', 'hr']} permissions={['locations.manage']}><Locations /></RoleRoute>} />
            <Route path="custom-fields"   element={<RoleRoute roles={['super_admin', 'company_admin', 'hr']} permissions={['custom_fields.manage']}><CustomFields /></RoleRoute>} />
            <Route path="webhooks"        element={<RoleRoute roles={['super_admin', 'company_admin']} permissions={['webhooks.manage']}><WebhooksAndTokens /></RoleRoute>} />
            <Route path="announcements"   element={<Announcements />} />
            <Route path="resignations"    element={<Resignations />} />
            <Route path="directory"       element={<Directory />} />
            <Route path="reimbursement-categories" element={<RoleRoute roles={['super_admin', 'company_admin', 'hr', 'accountant']}><ReimbursementCategories /></RoleRoute>} />
            <Route path="salary-templates" element={<RoleRoute roles={['super_admin', 'company_admin', 'hr']} permissions={['salary.templates.manage']}><SalaryTemplates /></RoleRoute>} />
            <Route path="ot-policies"     element={<RoleRoute roles={['super_admin', 'company_admin', 'hr']} permissions={['ot.manage']}><OtPolicies /></RoleRoute>} />
            <Route path="talent-pool"     element={<RoleRoute roles={['super_admin', 'company_admin', 'hr']} permissions={['recruitment.manage']}><TalentPool /></RoleRoute>} />
            <Route path="accounting-export" element={<RoleRoute roles={['super_admin', 'company_admin', 'accountant']} permissions={['payroll.export']}><AccountingExport /></RoleRoute>} />

            {/* Phase 5 */}
            <Route path="shift-swaps"    element={<ShiftSwaps />} />
            <Route path="skills"         element={<Skills />} />
            <Route path="my-peer-reviews" element={<MyPeerReviews />} />
            <Route path="internal-jobs"  element={<InternalJobs />} />

            {/* Parity gaps (migration 0037) */}
            <Route path="fbp"            element={<RoleRoute roles={['super_admin', 'company_admin', 'hr']} permissions={['salary.templates.manage']}><Fbp /></RoleRoute>} />
            <Route path="my-fbp"         element={<MyFbp />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

export default App;
